import { useCallback, useEffect, useRef, useState } from 'react';
import { loadTurnstile, type TurnstileApi } from '@lib/utils/turnstile';

/**
 * The anti-bot check of the public form, only when the server requires it (`siteKey` set): one invisible Turnstile
 * widget (it shows a challenge only when it needs one) in `ref`'s element; `token()` asks it for a fresh single-use
 * token, one request at a time. Without a site key, `token()` gives undefined and nothing is loaded.
 */
export function useCaptcha(siteKey: string | null | undefined) {
  const ref = useRef<HTMLDivElement>(null);
  const widget = useRef<{ api: TurnstileApi; id: string } | null>(null);
  const waiting = useRef<{ resolve: (t: string | undefined) => void } | null>(null);
  const chain = useRef<Promise<unknown>>(Promise.resolve());
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!siteKey || !ref.current) return;
    let gone = false;
    const el = ref.current;
    loadTurnstile()
      .then((api) => {
        if (gone) return;
        const settle = (t: string | undefined) => {
          waiting.current?.resolve(t);
          waiting.current = null;
        };
        const id = api.render(el, { sitekey: siteKey, execution: 'execute', appearance: 'interaction-only', language: 'id', callback: (t) => settle(t), 'error-callback': () => settle(undefined), 'expired-callback': () => undefined });
        widget.current = { api, id };
      })
      .catch(() => setFailed(true));
    return () => {
      gone = true;
      if (widget.current) widget.current.api.remove(widget.current.id);
      widget.current = null;
    };
  }, [siteKey]);

  const token = useCallback((): Promise<string | undefined> => {
    if (!siteKey) return Promise.resolve(undefined);
    const next = chain.current.then(
      () =>
        new Promise<string | undefined>((resolve) => {
          const w = widget.current;
          if (!w) return resolve(undefined);
          waiting.current = { resolve };
          w.api.reset(w.id);
          w.api.execute(w.id);
        }),
    );
    chain.current = next.catch(() => undefined);
    return next;
  }, [siteKey]);

  return { ref, token, required: !!siteKey, failed };
}
